import { useState } from 'react';
import { FileRecord, Category, ExtractedSource } from '@twinsketch/topika-model';
import { FaTrash, FaDownload, FaEye, FaCheck } from 'react-icons/fa';
import FileService from 'src/services/FileService';
import ApiService from 'src/services/ApiService';
import {
  WorkOrderPreviewData,
  WorkOrderPreviewService,
} from '../../../pages/workOrder/preview/WorkOrderPreviewService';
import { WorkOrderPreviewModal } from '../../../pages/workOrder/form/workOrderComponent/DataToComponentHelper';
import { GenericReportPreviewService } from '../../../pages/workOrder/preview/GenericReportPreviewService';
import { GenericReportPreviewModal } from '../../../pages/workOrder/preview/GenericReportPreviewModal';

const serviceMap = {
  [Category.MIXING_REPORT]: new GenericReportPreviewService(['일지']),
  [Category.COATING_REPORT]: new GenericReportPreviewService(['생산']),
  [Category.PRESS_REPORT]: new GenericReportPreviewService(['생산']),
  [Category.SLITTING_REPORT]: new GenericReportPreviewService(['생산']),
};

const categoryOrderMap = {
  [Category.WORK_ORDER]: 1,
  [Category.MIXING_REPORT]: 2,
  [Category.COATING_REPORT]: 3,
  [Category.PRESS_REPORT]: 4,
  [Category.SLITTING_REPORT]: 5,
  [Category.COA]: 6,
};

const categoryLabelMap = {
  [Category.WORK_ORDER]: 'Work Order',
  [Category.MIXING_REPORT]: 'Mixing Report',
  [Category.COATING_REPORT]: 'Coating Report',
  [Category.PRESS_REPORT]: 'Press Report',
  [Category.SLITTING_REPORT]: 'Slitting Report',
  [Category.COA]: 'COA',
};

const workOrderPreviewService = new WorkOrderPreviewService();

const RenderFileList = ({
  files,
  workOrderId,
  setError,
  workOrderData,
  setWorkOrderData,
  extractedFileIds,
}) => {
  const [previewType, setPreviewType] = useState<Category | null>(null);
  const [genericPreviewData, setGenericPreviewData] = useState<any[] | null>(null);
  const [workOrderfileData, setWorkOrderFileData] = useState<WorkOrderPreviewData | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);

  const workOrderName = workOrderData?.name;
  function gatherExtractedSources(workOrderData: any): string[] {
    const sources: ExtractedSource[] = [];

    const addSource = (source?: ExtractedSource) => {
      if (source?.sourceFileId) {
        sources.push(source);
      }
    };

    addSource(workOrderData?.extractedFrom);

    workOrderData?.mixingWorks?.forEach((w: any) => addSource(w.extractedFrom));
    workOrderData?.coatingWorks?.forEach((w: any) => addSource(w.extractedFrom));
    workOrderData?.pressWorks?.forEach((w: any) => addSource(w.extractedFrom));
    workOrderData?.slitWorks?.forEach((w: any) => addSource(w.extractedFrom));
    workOrderData?.coas?.forEach((w: any) => addSource(w.extractedFrom));

    // Return a formatted list of fileId-tabName pairs
    return sources
      .map(({ sourceFileId, sourceTabName }) =>
        sourceTabName && sourceFileId ? `${sourceFileId}-${sourceTabName}` : null
      )
      .filter(Boolean);
  }

  const alreadyExportedSource = new Set(gatherExtractedSources(workOrderData));

  const groupedFiles = files.reduce(
    (acc, file) => {
      acc[file.category] = [...(acc[file.category] || []), file];
      return acc;
    },
    {} as Record<Category, FileRecord[]>
  );

  const sortedCategories = Object.keys(groupedFiles).sort(
    (a, b) => (categoryOrderMap[a as Category] ?? 999) - (categoryOrderMap[b as Category] ?? 999)
  ) as Category[];

  const handleDeleteFile = async (fileId: string) => {
    try {
      await FileService.deleteFile(fileId, workOrderId);
      const updatedWorkOrder = await ApiService.fetchWorkOrderById(workOrderId);
      setWorkOrderData(updatedWorkOrder);
    } catch {
      setError('Failed to delete file.');
    }
  };

  const handleFileDownload = async (workOrderName, category, fileName) => {
    try {
      const file = files.find(f => f.category === category && f.fileName === fileName);
      const response = await fetch(file.s3Url);
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${workOrderName}-${category}-${fileName}`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch {
      setError('Download failed.');
    }
  };

  const handleFilePreview = async (
    fileId: string,
    fileName: string,
    fileUrl: string,
    fileCategory: Category,
    files: FileRecord[]
  ) => {
    try {
      if (fileCategory === Category.WORK_ORDER) {
        const file = files.find(f => f.category === fileCategory);
        const data = await workOrderPreviewService.getFilePreviewData(file.s3Url);
        setWorkOrderFileData(data);
        setPreviewType(Category.WORK_ORDER);
      } else if (serviceMap[fileCategory]) {
        const data = await serviceMap[fileCategory].getFilePreviewData(fileUrl);
        setGenericPreviewData(data);
        setPreviewType(fileCategory);
        setSelectedFileId(fileId);
        setSelectedFileName(fileName);
      }
      setPreviewVisible(true);
    } catch (error: any) {
      console.error('Error previewing file:', error);
      setError(error.message || 'Failed to preview the file.');
    }
  };

  return (
    <div style={{ padding: '12px' }}>
      {sortedCategories.map(category => (
        <div key={category} style={{ marginBottom: '16px' }}>
          <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '4px' }}>
            {categoryLabelMap[category]} ({groupedFiles[category].length})
          </div>
          <ul style={{ listStyleType: 'none', paddingLeft: '8px' }}>
            {groupedFiles[category].map(file => (
              <li
                key={file.id}
                style={{
                  padding: '6px 0',
                  borderBottom: '1px solid #eee',
                  display: 'flex',
                  maxWidth: '80%',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span
                      style={{
                        fontWeight: 500,
                        fontSize: '14px',
                        textDecoration: 'none',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '250px',
                      }}
                    >
                      {file.fileName}
                    </span>
                  </div>
                  {file.notes && (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      Notes: {file.notes} {file.sheetNames}
                    </div>
                  )}
                  {file.sheetNames?.length > 0 && (
                    <ul style={{ paddingLeft: '16px', marginTop: '6px' }}>
                      {file.sheetNames.map(sheetName => {
                        const key = `${file.id}-${sheetName}`;
                        const isExtracted = alreadyExportedSource.has(key);
                        return (
                          <li
                            key={key}
                            style={{
                              fontSize: '12px',
                              color: '#333',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            {sheetName}
                            {isExtracted && (
                              <FaCheck title="Extracted" style={{ color: 'green' }} />
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <FaEye
                    size={12}
                    style={{ cursor: 'pointer', color: '#555', opacity: 0.8 }}
                    title="Preview"
                    onClick={() =>
                      handleFilePreview(file.id, file.fileName, file.s3Url, file.category, files)
                    }
                  />
                  <FaDownload
                    size={12}
                    style={{ cursor: 'pointer', color: '#555', opacity: 0.8 }}
                    title="Download"
                    onClick={() => handleFileDownload(workOrderName, category, file.fileName)}
                  />
                  <FaTrash
                    size={12}
                    style={{ cursor: 'pointer', color: '#555', opacity: 0.8 }}
                    title="Delete"
                    onClick={() => handleDeleteFile(file.id)}
                  />
                </div>
              </li>
            ))}
          </ul>
          {previewVisible && previewType === 'WORK_ORDER' && workOrderfileData && (
            <WorkOrderPreviewModal
              data={workOrderfileData}
              fileId={selectedFileId}
              workOrderData={workOrderData}
              workOrderId={workOrderId}
              onClose={() => {
                setPreviewVisible(false);
                setPreviewType(null);
              }}
            />
          )}
          {previewVisible && previewType !== Category.WORK_ORDER && genericPreviewData && (
            <GenericReportPreviewModal
              data={genericPreviewData}
              fileId={selectedFileId}
              fileName={selectedFileName}
              workOrderId={workOrderId}
              fileCategory={previewType}
              exportedSource={alreadyExportedSource}
              onClose={() => {
                setPreviewVisible(false);
                setPreviewType(null);
              }}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default RenderFileList;
